import React from "react";
import * as d3 from "d3";
import moment from "moment";
import d3_annotation from "d3-svg-annotation";
import "../styles/components/round-chart.scss";

const width = Math.min(window.innerWidth, 1500) - 200;
const height = Math.min(window.innerHeight - 100, width);

class RoundChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  draw = () => {
    // ---------------------------------------------- //
    // ----------------- Generators ----------------- //
    // ---------------------------------------------- //
    var arc = (outerRadius, innerRadius = (0.4 * height) / 2, radius = 5) => {
      return d3
        .arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(radius);
    };

    var labelArcs = d3
      .arc()
      .innerRadius((0.47 * height) / 2)
      .outerRadius((0.47 * height) / 2);

    var pie = d3
      .pie()
      .value((d) => d.duration)
      .sort(null);

    var line = d3
      .lineRadial()
      .angle((d) =>
        x(
          moment(d.time)
            .utc()
            .diff(
              moment(d.time).utc().hour(0).minute(0).seconds(0).millisecond(0)
            )
        )
      )
      .radius((d) => y(d.probe_duration_seconds));

    // ---------------------------------------------- //
    // -----------------   Scales   ----------------- //
    // ---------------------------------------------- //

    var x = (d) => {
      const data = moment(d.time)
        .utc()
        .diff(moment(d.time).utc().hour(0).minute(0).seconds(0).millisecond(0));

      return d3
        .scaleLinear()
        .domain([0, 24 * 60 * 60 * 1000])
        .range([0, 2 * Math.PI])(data);
    };

    var colorScale = d3
      .scaleDiverging(d3.interpolateRdYlGn)
      .domain([0, 50, 100]);

    var colors = d3
      .scaleOrdinal()
      .range(d3.quantize(d3.interpolateHcl("#fafa6e", "#2A4858"), 10));

    var colorDomain = () => {
      const extent = d3.extent(
          this.props.rawData,
          (d) => d.probe_duration_seconds
        ),
        interpolated = d3.interpolate(...extent);

      return d3.quantize(interpolated, 7);
    };

    var rtColor = d3.scaleLinear(
      colorDomain(),
      d3.quantize(d3.interpolateSpectral, 7).reverse()
    );

    var heightScaler = d3.scaleLinear().domain([0, 100]).range([0.4, 0.6]);

    var xAxis = (g) =>
      g.attr("text-anchor", "middle").call((g) =>
        g
          .selectAll("g")
          .data([
            "06:00",
            "08:00",
            "10:00",
            "12:00",
            "14:00",
            "16:00",
            "18:00",
            "20:00",
            "22:00",
            "00:00",
            "02:00",
            "04:00",
          ])
          .join("g")
          .attr(
            "transform",
            (d, i, arr) => `
            rotate(${(i * 360) / arr.length})
            translate(${(0.35 * height) / 2},0)
          `
          )
          .call((g) =>
            g
              .append("line")
              .attr("x1", -5)
              .attr("x2", (0.7 * height) / 2 - (0.35 * height) / 2)
              .attr("stroke", "rgba(102,188,239,.2)")
              // .attr("stroke", "#fff")
              .attr("stroke-opacity", 0.7)
          )
          .call((g) =>
            g
              .append("text")
              .attr("transform", (d, i, arr) =>
                ((i * 360) / arr.length) % 360 > 180
                  ? "rotate(90)translate(0,16)"
                  : "rotate(-90)translate(0,-9)"
              )
              .style("font-family", "sans-serif")
              .style("font-size", 12)
              .attr("fill", "#fff")
              .attr("opacity", 0.7)
              .text((d) => d)
          )
      );

    var xAxisRT = (g) =>
      g.attr("text-anchor", "middle").call((g) =>
        g
          .selectAll("g")
          .data([
            "06:00",
            "08:00",
            "10:00",
            "12:00",
            "14:00",
            "16:00",
            "18:00",
            "20:00",
            "22:00",
            "00:00",
            "02:00",
            "04:00",
          ])
          .join("g")
          .attr(
            "transform",
            (d, i, arr) => `
              rotate(${(i * 360) / arr.length})
              translate(${(0.7 * height) / 2},0)
            `
          )
          .call((g) =>
            g
              .append("line")
              .attr("x1", 1)
              .attr("x2", (0.27 * height) / 2)
              .attr("stroke", "rgba(102,188,239,.2)")
              // .attr("stroke", "#fff")
              .attr("stroke-opacity", 0.7)
          )
      );

    var y = d3
      .scalePow()
      .exponent(0.75)
      .domain(d3.extent(this.props.rawData, (d) => d.probe_duration_seconds))
      .range([(0.67 * height) / 2, (0.95 * height) / 2]);

    var yAxis = (g) =>
      g
        .attr("text-anchor", "middle")
        .attr("font-family", "sans-serif")
        .attr("font-size", 9)
        .call((g) =>
          g
            .selectAll("g")
            .data(y.ticks().reverse())
            .join("g")
            .attr("fill", "none")
            .call((g) =>
              g
                .append("circle")
                .attr("stroke", "rgba(102,188,239,.2)")
                .attr("stroke-opacity", 0.4)
                .attr("r", y)
            )
            .call((g) =>
              g
                .append("text")
                .attr("y", (d) => -y(d))
                .attr("dy", "0.35em")
                .attr("stroke", "#21242e")
                .attr("stroke-width", 2)
                .text((x, i) => `${x.toFixed(0)}${i ? "" : " sec"}`)
                .clone(true)
                .attr("y", (d) => y(d))
                .selectAll(function () {
                  return [this, this.previousSibling];
                })
                .clone(true)
                .attr("fill", "rgba(255, 255, 255, .7)")
                .attr("stroke", "none")
            )
        );

    // ---------------------------------------------- //
    // -----------------   Drawing  ----------------- //
    // ---------------------------------------------- //

    const svg = d3
      .select(this._rootNode)
      .attr("width", width)
      .attr("height", height)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round");

    const defs = svg.append("defs");
    let shadowID;

    defs
      .append("filter") // drop shadow definition
      .attr("id", "shadow")
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 0)
      .attr("stdDeviation", 3)
      .attr("flood-color", "black")
      .attr("flood-opacity", 0.5);

    //Create a shadow filter
    const filter = defs.append("filter").attr("id", "shadow");
    filter
      .append("feColorMatrix")
      .attr("type", "matrix")
      .attr("values", "0 0 0 0   0 0 0 0 0   0 0 0 0 0   0 0 0 0 0.1 0");
    filter
      .append("feGaussianBlur")
      .attr("stdDeviation", "5")
      .attr("result", "coloredBlur");
    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // ------------BG------------
    const bgG = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    //Create background circle
    bgG
      .append("circle")
      .attr("r", height / 2)
      .style("fill", this.props.chartBgColor)
      .style("filter", "url(#shadow)");

    //////////////////////////////////////////////////////////////
    ///////////////////////// Add title //////////////////////////
    //////////////////////////////////////////////////////////////

    const titleG = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    titleG
      .append("text")
      .attr("class", "title-top")
      .attr("y", -5)
      .text(this.props.monitorName);

    titleG
      .append("text")
      .attr("class", "title-bottom")
      .attr("y", 15)
      .text("per day");

    // ------------UPTIME------------
    const uptimeG = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    var title = (d) => {
      if (d.status === "up") return `Status Code: ${d.status}`;

      return `Status Code: ${d.status}\nFrom: ${d.from_time}\nTo: ${
        d.to_time
      }\nDuration: ${moment.utc(d.duration * 1000).format("HH:mm:ss")}`;
    };

    uptimeG
      .append("path")
      .style("fill", "#15181e")
      .attr(
        "d",
        arc(
          (0.66 * height) / 2,
          (0.61 * height) / 2
        )(pie([{ duration: 100 }])[0])
      );

    const downTimeArcs = pie(this.props.downtimeData);

    uptimeG
      .selectAll("path.uptime")
      .data(downTimeArcs)
      .join("path")
      .attr("class", "uptime")
      .style("stoke", "#15181e")
      .style("stroke-width", 0)
      .style("fill", (d) =>
        d.data.status === "up" ? "rgb(105, 201, 129)" : "rgb(233, 92, 96)"
      )
      .attr("data-tooltip", (d) => title(d.data))
      .attr("d", (d) => arc((0.65 * height) / 2, (0.62 * height) / 2, 0)(d));

    // ------------AXIS------------
    const container = svg
      .append("g")
      .attr("class", "container")
      .attr("transform", `translate(${width / 2},${height / 2})`)
      .style("font-size", 10)
      .style("font-family", "sans-serif");

    container.append("g").call(xAxis);

    // ------------APDEX------------
    const apdexG = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    apdexG
      .append("path")
      .style("fill", "#15181e")
      .attr("d", arc((0.6 * height) / 2)(pie([{ duration: 100 }])[0]));

    const pieArcs = pie(this.props.apdexData);

    apdexG
      .selectAll("path.apdex")
      .data(pieArcs)
      .join("path")
      .attr("class", "apdex")
      .style("stroke", "#15181e")
      .style("stroke-width", 1)
      .style("fill", (d) => colorScale(d.data.value))
      .attr("d", (d) => arc((heightScaler(d.data.value) * height) / 2)(d));

    const apdexText = svg
      .append("g")
      .attr("class", "labels-container")
      .attr("transform", `translate(${width / 2},${height / 2})`)
      .selectAll("text")
      .data(pieArcs)
      .join("text")
      .attr("transform", (d) => `translate(${labelArcs.centroid(d)})`)
      .attr("text-anchor", "middle")
      .attr("x", 0)
      .style("font-family", "sans-serif")
      .style("font-size", 9)
      .style("fill", "#222")
      .attr("dy", ".35em")
      .text((d) => `${d.data.value}%`);

    // ------------RT LINE------------
    const rtLineG = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    var rtTitle = (d) => `Respnse time: ${d.probe_duration_seconds}
Time: ${d.time}
Time inserted: ${d.time_inserted}
Time: ${moment(d.time)}
Prober id: ${d.prober_id}
`;

    rtLineG
      .selectAll("circle.rtLine")
      .data(this.props.rawData)
      .join("circle")
      .attr("class", "rt")
      .attr("cx", (d) => Math.sin(x(d)) * y(d.probe_duration_seconds))
      .attr("cy", (d) => Math.cos(x(d)) * y(d.probe_duration_seconds))
      .attr("r", (d) => Math.sqrt(d.probe_duration_seconds))
      .attr("fill", (d) => rtColor(d.probe_duration_seconds))
      .attr("stroke", (d) =>
        d.probe_duration_seconds > 5 ? this.props.background : "transparent"
      )
      .attr("stroke-width", 0.2)
      .attr("opacity", 0.8)
      .attr("data-tooltip", (d) => rtTitle(d));

    // ------------AXIS------------

    container.append("g").call(xAxisRT);

    container.append("g").call(yAxis);
  };

  componentDidMount() {
    this.draw();
    this.tooltip();
  }

  annotate = () => {
    const annotations = [
      {
        type: d3_annotation.annotationCustomType(
          d3_annotation.annotationCalloutCircle,
          {
            className: "annotate",
            connector: {
              type: "elbow",
              end: "dot",
            },
            note: {
              lineType: "vertical",
              align: "dynamic",
            },
          }
        ),
        note: {
          label: "I don't know what happend here",
          title: "Unusual Spikes",
          wrap: 150,
        },
        subject: {
          radius: 30,
          radiusPadding: 5,
        },
        x: 475,
        y: 45,
        dy: 0,
        dx: 275,
      },
    ].map(function (d) {
      d.color = this.props.annotateColor;
      return d;
    });

    const makeAnnotations = d3_annotation
      .annotation()
      .type(d3_annotation.annotationLabel)
      .annotations(annotations);

    d3.select("svg")
      .append("g")
      .attr("class", "annotation-group")
      .call(makeAnnotations);
  };

  tooltip = () => {
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "svg-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .text("I'm a circle!");

    d3.selectAll(".uptime")
      .on("mouseover", function () {
        tooltip.text(this.dataset.tooltip);
        return tooltip.style("visibility", "visible");
      })
      .on("mousemove", function () {
        return tooltip
          .style("top", d3.event.pageY - 10 + "px")
          .style("left", d3.event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        tooltip.text("");
        return tooltip.style("visibility", "hidden");
      });

    d3.selectAll(".rt")
      .on("mouseover", function () {
        tooltip.text(this.dataset.tooltip);
        return tooltip.style("visibility", "visible");
      })
      .on("mousemove", function () {
        return tooltip
          .style("top", d3.event.pageY - 10 + "px")
          .style("left", d3.event.pageX + 10 + "px");
      })
      .on("mouseout", function () {
        tooltip.text("");
        return tooltip.style("visibility", "hidden");
      });
  };

  // shouldComponentUpdate(props) {
  //   return false;
  // }

  _setRef(componentNode) {
    this._rootNode = componentNode;
  }

  render() {
    return (
      <div className="svg-container">
        <svg ref={this._setRef.bind(this)}></svg>
      </div>
    );
  }
}

export default RoundChart;
